import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Button, Space, Typography, Avatar, Badge, Dropdown, List, message } from 'antd';
import { LogoutOutlined, UserOutlined, MenuOutlined, ArrowLeftOutlined, BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { logout } from '../../../redux/slices/authSlice';
import { acceptChatRequest, fetchContacts, fetchIncomingRequests } from '../../../redux/slices/messageSlice';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

const Header = ({ onUserProfileClick, collapsed, setCollapsed, isMobile }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const { incomingRequests, contacts } = useAppSelector((state) => state.messages);
    const [dismissedNotificationKeys, setDismissedNotificationKeys] = useState([]);

    useEffect(() => {
      dispatch(fetchIncomingRequests());
      dispatch(fetchContacts());
      const interval = setInterval(() => {
        dispatch(fetchIncomingRequests());
        dispatch(fetchContacts());
      }, 30000);
      return () => clearInterval(interval);
    }, [dispatch]);

    const followRequestNotifications = useMemo(
      () =>
        (incomingRequests || [])
          .filter((item) => Boolean(item?.id))
          .map((item) => ({
            key: `request-${item.id}`,
            type: 'follow_request',
            requestId: item.id,
            text: `${item?.requester?.name || 'A user'} sent you a follow request.`,
          })),
      [incomingRequests]
    );

    const messageNotifications = useMemo(
      () =>
        (contacts || [])
          .filter((contact) => Number(contact?.unreadCount || 0) > 0)
          .map((contact) => ({
            key: `message-${contact.id}`,
            type: 'message',
            contactId: contact.id,
            text: `New message received from ${contact?.name || 'a user'}.`,
          })),
      [contacts]
    );

    const allNotifications = useMemo(
      () => [...followRequestNotifications, ...messageNotifications],
      [followRequestNotifications, messageNotifications]
    );

    const visibleNotifications = useMemo(
      () => allNotifications.filter((item) => !dismissedNotificationKeys.includes(item.key)),
      [allNotifications, dismissedNotificationKeys]
    );

    useEffect(() => {
      const availableKeys = new Set(allNotifications.map((item) => item.key));
      setDismissedNotificationKeys((prev) => prev.filter((key) => availableKeys.has(key)));
    }, [allNotifications]);

    const handleLogout = async () => {
      try {
        await dispatch(logout()).unwrap();
        navigate('/login');
      } catch (error) {
        console.error('Logout failed:', error);
      }
    };

    const handleUserClick = () => {
      if (onUserProfileClick) {
        onUserProfileClick();
      }
    };

    const getRoleHomeRoute = () => {
      if (user?.role === 'ADMIN') return '/admin/dashboard';
      if (user?.role === 'REPRESENTATIVE') return '/representative/dashboard';
      if (user?.role === 'VOLUNTEER') return '/volunteer/dashboard';
      return '/citizen/dashboard';
    };

    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(getRoleHomeRoute());
      }
    };

    const handleAcceptFromNotification = async (requestId) => {
      const result = await dispatch(acceptChatRequest(requestId));
      if (acceptChatRequest.fulfilled.match(result)) {
        message.success('Follow request accepted');
        setDismissedNotificationKeys((prev) => [...new Set([...prev, `request-${requestId}`])]);
        dispatch(fetchIncomingRequests());
        dispatch(fetchContacts());
        return;
      }
      message.error(result.payload?.message || 'Could not accept follow request');
    };

    const getRoleMessagesRoute = () => {
      if (user?.role === 'ADMIN') return '/admin/messages';
      if (user?.role === 'REPRESENTATIVE') return '/representative/messages';
      if (user?.role === 'VOLUNTEER') return '/volunteer/messages';
      return '/citizen/messages';
    };

    const handleReadNotification = (notificationKey) => {
      setDismissedNotificationKeys((prev) => [...new Set([...prev, notificationKey])]);
    };

    const handleOpenMessageNotification = (notification) => {
      handleReadNotification(notification.key);
      navigate(getRoleMessagesRoute());
    };

    const notificationDropdown = (
      <div
        style={{
          width: 360,
          maxWidth: '90vw',
          maxHeight: 360,
          overflowY: 'auto',
          padding: 8,
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
        }}
      >
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Notifications
        </Text>
        <List
          dataSource={visibleNotifications}
          locale={{ emptyText: 'No new notifications' }}
          renderItem={(item) => (
            <List.Item
              style={{ alignItems: 'flex-start' }}
              actions={
                item.type === 'follow_request'
                  ? [
                      <Button key="read" size="small" onClick={() => handleReadNotification(item.key)}>
                        Mark as Read
                      </Button>,
                      <Button
                        key="accept"
                        size="small"
                        type="primary"
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        onClick={() => handleAcceptFromNotification(item.requestId)}
                      >
                        Accept
                      </Button>,
                    ]
                  : [
                      <Button key="read-message" size="small" onClick={() => handleReadNotification(item.key)}>
                        Mark as Read
                      </Button>,
                      <Button key="open-message" size="small" type="primary" onClick={() => handleOpenMessageNotification(item)}>
                        Open
                      </Button>,
                    ]
              }
            >
              <Text>{item.text}</Text>
            </List.Item>
          )}
        />
      </div>
    );

    return (
      <AntHeader style={{ padding: isMobile ? '0 12px' : '0 16px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isMobile ? (
          <>
            <Space size={8} style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: '18px', padding: 0, width: 32, minWidth: 32 }}
              />
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                title="Go Back"
                style={{ fontSize: '16px', padding: 0, width: 32, minWidth: 32 }}
              />
              <Avatar
                icon={<UserOutlined />}
                onClick={handleUserClick}
                style={{ cursor: 'pointer', backgroundColor: '#1890ff', flexShrink: 0 }}
                title="Click to view profile"
              />
              <Text
                strong
                onClick={handleUserClick}
                style={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                  cursor: 'pointer'
                }}
              >
                {user?.name} <Text type="secondary" style={{ fontSize: 12 }}>({user?.role})</Text>
              </Text>
            </Space>
            <Dropdown popupRender={() => notificationDropdown} trigger={['click']}>
              <Button
                type="text"
                style={{ padding: 0, width: 32, minWidth: 32 }}
                icon={
                  <Badge count={visibleNotifications.length} size="small">
                    <BellOutlined style={{ fontSize: '18px' }} />
                  </Badge>
                }
              />
            </Dropdown>
            <Button
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              type="text"
              size="small"
              style={{ marginLeft: 8, flexShrink: 0 }}
            />
          </>
        ) : (
          <>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              title="Go Back"
            />
            <Space>
              <div
                onClick={handleUserClick}
                title="Click to view profile"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                <UserOutlined />
                <Text strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{user?.name}</Text>
                <Text type="secondary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>({user?.role})</Text>
              </div>
              <Dropdown popupRender={() => notificationDropdown} trigger={['click']}>
                <Button type="text" icon={<Badge count={visibleNotifications.length} size="small"><BellOutlined /></Badge>} />
              </Dropdown>
              <Button
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                type="text"
              >
                Logout
              </Button>
            </Space>
          </>
        )}
      </AntHeader>
    );
  };

  export default Header;
